import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DayPicker, type DayButtonProps } from 'react-day-picker';
import { useEntriesStore } from '../../stores';
import { Button, Input } from '../ui';
import { api } from '../../lib/api';

interface EntriesSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

function toDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function makeDayButton(entryDays: Set<string>) {
  return function CustomDayButton({ day, modifiers, className, children, ...props }: DayButtonProps) {
    const hasEntry = entryDays.has(day.isoDate);
    let extraClass = '';
    if (modifiers.selected) {
      extraClass = ' bg-primary-600 text-white hover:bg-primary-700';
    } else if (hasEntry) {
      extraClass = ' ring-1 ring-primary-600';
    }
    if (modifiers.today) {
      extraClass += ' font-bold';
    }
    return (
      <button {...props} className={className + extraClass}>
        {children}
      </button>
    );
  };
}

export function EntriesSidebar({ isOpen, onClose }: EntriesSidebarProps) {
  const { t } = useTranslation();
  const { filters, setFilters, isSearching, setIsSearching, tags, fetchTags } = useEntriesStore();
  const [searchInput, setSearchInput] = useState(filters.search);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [entryDays, setEntryDays] = useState<Set<string>>(new Set());
  const [selectedDay, setSelectedDay] = useState<Date | undefined>(undefined);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    if (searchInput !== filters.search && searchInput.length >= 3) {
      setIsSearching(true);
      debounceTimerRef.current = setTimeout(() => {
        setFilters({ search: searchInput });
      }, 1000);
    } else if (searchInput !== filters.search && searchInput.length < 3 && filters.search) {
      setIsSearching(true);
      debounceTimerRef.current = setTimeout(() => {
        setFilters({ search: '' });
      }, 1000);
    }

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [searchInput, filters.search, setFilters, setIsSearching]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    const start = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const end = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0, 23, 59, 59, 999);
    api
      .listEntryDates({
        startDate: start.toISOString(),
        endDate: end.toISOString()
      })
      .then((response) => {
        setEntryDays(new Set(response.dates.map((iso) => toDateKey(new Date(iso)))));
      })
      .catch(() => {});
  }, [calendarMonth]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setIsSearching(true);
    setFilters({ search: searchInput });
  };

  const handleDaySelect = (day: Date | undefined) => {
    setSelectedDay(day);
    setFilters({ dateFilter: day ? toDateKey(day) : null });
  };

  const toggleTag = (tag: string) => {
    const isSelected = filters.tags.includes(tag);
    setFilters({
      tags: isSelected
        ? filters.tags.filter((t) => t !== tag)
        : [...filters.tags, tag],
    });
  };

  const components = { DayButton: makeDayButton(entryDays) };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-x-0 bottom-0 top-14 z-40 bg-black/20 md:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={`fixed left-0 top-14 z-50 h-[calc(100%-3.5rem)] w-64 overflow-y-auto border-r border-paper-300 bg-paper-100 shadow-md transition-transform duration-200 md:z-30 md:translate-x-0 md:shadow-none ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
      <div className="p-4 space-y-4">
        <Link to="/entries/new" className="block">
          <Button className="w-full">
            <svg aria-hidden="true" className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('entries.newEntry')}
          </Button>
        </Link>

        <form onSubmit={handleSearch}>
          <div className="relative">
            <Input
              placeholder={t('entries.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pr-8"
            />
            {isSearching && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
              </div>
            )}
          </div>
        </form>

        <div className="rounded border border-paper-300 bg-paper-100 px-1 py-2">
          <DayPicker
            mode="single"
            selected={selectedDay}
            onSelect={handleDaySelect}
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            fixedWeeks
            weekStartsOn={1}
            components={components}
            classNames={{
              root: 'w-full',
              months: 'w-full',
              month: 'w-full',
              month_caption: 'flex items-center justify-between mb-2 px-1',
              caption_label: 'text-xs font-medium text-ink capitalize',
              nav: 'flex gap-0.5',
              button_previous:
                'flex h-5 w-5 items-center justify-center rounded text-ink-500 hover:bg-paper-200 hover:text-ink transition-colors',
              button_next:
                'flex h-5 w-5 items-center justify-center rounded text-ink-500 hover:bg-paper-200 hover:text-ink transition-colors',
              month_grid: 'w-full table-fixed',
              weekdays: '',
              weekday: 'pb-1 text-center text-[10px] font-normal text-ink-500',
              week: '',
              day: 'p-0.5 text-center',
              day_button:
                'mx-auto flex h-7 w-7 items-center justify-center rounded-full text-xs text-ink transition-colors hover:bg-paper-200',
              selected: '',
              today: '',
              outside: '!text-ink-300',
              disabled: '!cursor-default !text-ink-300',
            }}
          />
        </div>

        {tags.length > 0 && (
          <div>
            <h3 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-500">
              {t('entries.filterByTag')}
            </h3>
            <div className="flex max-h-32 flex-wrap gap-1 overflow-y-auto">
              {tags.map((tag) => {
                const isSelected = filters.tags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    className={`rounded-sm border px-2 py-0.5 text-xs transition-colors ${
                      isSelected
                        ? 'border-primary-600 bg-primary-600 text-white'
                        : 'border-paper-300 bg-paper-200 text-ink-600 hover:border-paper-400'
                    }`}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
            {filters.tags.length > 0 && (
              <button
                type="button"
                onClick={() => setFilters({ tags: [] })}
                className="mt-1 text-xs text-primary-600 hover:underline"
              >
                {t('entries.clearTagFilter')}
              </button>
            )}
          </div>
        )}

        <div className="overflow-hidden rounded-sm border border-paper-300">
          <button
            type="button"
            onClick={() => setFilters({ sortOrder: 'desc' })}
            className={`w-full px-3 py-2 text-left text-sm transition-colors ${
              filters.sortOrder === 'desc'
                ? 'bg-paper-200 text-ink font-medium'
                : 'text-ink-500 hover:bg-paper-200 hover:text-ink'
            }`}
          >
            {t('entries.sortDesc')}
          </button>
          <button
            type="button"
            onClick={() => setFilters({ sortOrder: 'asc' })}
            className={`w-full border-t border-paper-300 px-3 py-2 text-left text-sm transition-colors ${
              filters.sortOrder === 'asc'
                ? 'bg-paper-200 text-ink font-medium'
                : 'text-ink-500 hover:bg-paper-200 hover:text-ink'
            }`}
          >
            {t('entries.sortAsc')}
          </button>
        </div>

        <button
          type="button"
          onClick={() => setFilters({ noLocation: !filters.noLocation })}
          className={`w-full overflow-hidden rounded-sm border px-3 py-2 text-left text-sm transition-colors ${
            filters.noLocation
              ? 'border-primary-600 bg-primary-600 text-white'
              : 'border-paper-300 bg-paper-100 text-ink-600 hover:border-paper-400'
          }`}
        >
          {t('entries.noLocationFilter')}
        </button>
      </div>
    </aside>
    </>
  );
}
